/**
 * servicoGerenciadorProcessos
 *
 * RESPONSABILIDADE:
 * - Gerenciar a lista de processos (criação, leitura, atualização, exclusão) no frontend.
 * - Persistência simples em localStorage para permitir navegação e retomada.
 *
 * OBSERVAÇÕES DE DESIGN:
 * - Implementação deliberadamente simples (in-memory + localStorage) para interface com o Dashboard.
 * - Em futuro desenvolvimento, este serviço deve ser substituído por um backend dedicado
 *   ou por uma camada de sincronização com o servidor.
 */

export interface Processo {
  /** Identificador único do processo (UUID) */
  id: string;
  /** Tipo do processo (ex: "analise", "analise-peticao") */
  tipo: string;
  /** Nome amigável do processo */
  nome: string;
  /** Status do processo: 'pendente' | 'em_andamento' | 'concluido' | 'erro' */
  status: 'pendente' | 'em_andamento' | 'concluido' | 'erro';
  /** Índice da etapa atual (0-based) */
  etapaAtual: number;
  /** Progresso percentual (0-100) */
  progresso: number;
  /** Dados arbitrários do processo (metadados, referências a documentos, etc.) */
  dados?: Record<string, unknown>;
  /** Histórico de eventos (mensagens curtas) */
  historico?: string[];
  /** TAREFA-224: Tipo de vertical/fluxo do processo (analise_processual, criacao_peticao, etc.) */
  tipoVertical?: string;
  // MULTI-TENANT: Informações do dono/organização
  /** ID do usuário que criou o processo */
  usuarioId?: string;
  /** Nome do usuário que criou (para exibição) */
  nomeUsuario?: string;
  /** ID da organização (se compartilhado) */
  organizacaoId?: string;
  /** Se o processo é do próprio usuário logado */
  ehMeu?: boolean;
}

const STORAGE_KEY = 'ai_platform_processos_v1';

function lerStorage(): Processo[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as Processo[];
  } catch (err) {
    // Se o parse falhar, resetamos a storage para evitar comportamento inconsistente
    console.error('Falha ao ler processos do localStorage, resetando.', err);
    localStorage.removeItem(STORAGE_KEY);
    return [];
  }
}

function gravarStorage(lista: Processo[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(lista));
}

/**
 * Cria um novo processo e persiste localmente.
 * Retorna o processo criado.
 */
export function criarProcesso(novo: Omit<Processo, 'id'>): Processo {
  const lista = lerStorage();
  const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  const processo: Processo = { id, ...novo } as Processo;
  lista.unshift(processo);
  gravarStorage(lista);
  return processo;
}

/** Lista todos os processos ordenados por criação (mais recente primeiro) */
export function listarProcessos(): Processo[] {
  return lerStorage();
}

/** Obter processo por id */
export function obterProcesso(id: string): Processo | undefined {
  const lista = lerStorage();
  return lista.find(p => p.id === id);
}

/** Atualiza um processo (substitui campos informados) */
export function atualizarProcesso(id: string, patch: Partial<Processo>): Processo | undefined {
  const lista = lerStorage();
  const idx = lista.findIndex(p => p.id === id);
  if (idx === -1) return undefined;
  const atualizado = { ...lista[idx], ...patch };
  lista[idx] = atualizado;
  gravarStorage(lista);
  return atualizado;
}

/** Exclui um processo e retorna true se excluído */
export function excluirProcesso(id: string): boolean {
  const lista = lerStorage();
  const novo = lista.filter(p => p.id !== id);
  if (novo.length === lista.length) return false;
  gravarStorage(novo);
  return true;
}

/** Função utilitária para criação rápida de processo (ex: demo) */
export function criarProcessoExemplo(): Processo {
  return criarProcesso({
    tipo: 'analise',
    nome: `Processo Exemplo - ${new Date().toLocaleString()}`,
    status: 'pendente',
    etapaAtual: 0,
    progresso: 0,
    dados: {},
    historico: ['Criado localmente']
  });
}
